<template>
  <div class="rental-page">
    <!-- ===== 页面头部 ===== -->
    <section class="rental-hero">
      <div class="rental-hero-overlay"></div>
      <div class="rental-hero-content">
        <h1 class="rental-hero-title">
          {{ $t("rental.title1")
          }}<span class="highlight">{{ $t("rental.title2") }}</span>
        </h1>
        <p class="rental-hero-desc">{{ $t("rental.desc") }}</p>
        <div class="rental-hero-stats">
          <div class="hero-stat">
            <span class="hero-stat-number">{{ totalServers }}</span>
            <span class="hero-stat-label">{{ $t("rental.statServers") }}</span>
          </div>
          <div class="hero-stat-divider"></div>
          <div class="hero-stat">
            <span class="hero-stat-number">99.9%</span>
            <span class="hero-stat-label">{{
              $t("rental.statAvailability")
            }}</span>
          </div>
          <div class="hero-stat-divider"></div>
          <div class="hero-stat">
            <span class="hero-stat-number">24/7</span>
            <span class="hero-stat-label">{{ $t("rental.statSupport") }}</span>
          </div>
        </div>
      </div>

      <!-- 第一页底部 轮播 -->
      <div></div>
    </section>

    <!-- ===== 筛选 & 搜索栏 ===== -->
    <section class="rental-filter-section">
      <div class="rental-filter-container">
        <div class="filter-left">
          <div class="search-box">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path
                d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
              />
            </svg>
            <input
              type="text"
              v-model="searchKeyword"
              :placeholder="$t('rental.searchPlaceholder')"
              class="search-input"
              @input="handleSearch"
            />
          </div>

          <div class="filter-tags">
            <button
              class="filter-tag"
              :class="{ active: activeFilter === 'all' }"
              @click="
                activeFilter = 'all';
                handleFilter();
              "
            >
              {{ $t("rental.filterAll") }}
            </button>
            <button
              class="filter-tag"
              :class="{ active: activeFilter === 'hot' }"
              @click="
                activeFilter = 'hot';
                handleFilter();
              "
            >
              {{ $t("rental.filterHot") }}
            </button>
            <button
              class="filter-tag"
              :class="{ active: activeFilter === 'recommend' }"
              @click="
                activeFilter = 'recommend';
                handleFilter();
              "
            >
              {{ $t("rental.filterRecommend") }}
            </button>
            <button
              class="filter-tag"
              :class="{ active: activeFilter === 'new' }"
              @click="
                activeFilter = 'new';
                handleFilter();
              "
            >
              {{ $t("rental.filterNew") }}
            </button>
            <button
              class="filter-tag"
              :class="{ active: activeFilter === 'pending' }"
              @click="
                activeFilter = 'pending';
                handleFilter();
              "
            >
              ⏳ {{ $t("rental.statusPending") }}
            </button>
            <button
              class="filter-tag"
              :class="{ active: activeFilter === 'preOrder' }"
              @click="
                activeFilter = 'preOrder';
                handleFilter();
              "
            >
              📦 {{ $t("serverList.available") }}
            </button>
            <button
              class="filter-tag"
              :class="{ active: activeFilter === 'active' }"
              @click="
                activeFilter = 'active';
                handleFilter();
              "
            >
              ▶️ {{ $t("serverList.yunxz") }}
            </button>
          </div>
        </div>

        <div class="filter-right">
          <select v-model="sortBy" class="sort-select" @change="handleSort">
            <option value="default">
              {{ $t("rental.sortDefault") }}
            </option>
            <option value="value-asc">{{ $t("rental.sortValueAsc") }}</option>
            <option value="value-desc">{{ $t("rental.sortValueDesc") }}</option>
            <option value="income-asc">{{ $t("rental.sortIncomeAsc") }}</option>
            <option value="income-desc">{{ $t("rental.sortIncomeDesc") }}</option>
          </select>

          <div class="view-toggle">
            <button
              class="view-btn"
              :class="{ active: viewMode === 'grid' }"
              @click="viewMode = 'grid'"
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="currentColor"
              >
                <path d="M3 3h8v8H3zm0 10h8v8H3zm10-10h8v8h-8zm0 10h8v8h-8z" />
              </svg>
            </button>
            <button
              class="view-btn"
              :class="{ active: viewMode === 'list' }"
              @click="viewMode = 'list'"
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="currentColor"
              >
                <path d="M3 13h18v2H3zm0-6h18v2H3zm0 12h18v2H3z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== 服务器列表 ===== -->
    <section class="rental-list-section">
      <div class="rental-list-container">
        <div class="rental-result-count">
          {{ $t("rental.resultCount", { count: totalServers }) }}
        </div>

        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <span>{{ $t("rental.loading") }}</span>
        </div>

        <div v-else-if="filteredServers.length === 0" class="empty-state">
          <div class="empty-icon">🔍</div>
          <p>{{ $t("rental.empty") }}</p>
        </div>

        <!-- ===== 网格视图 ===== -->
        <div v-else-if="viewMode === 'grid'" class="rental-grid">
          <div
            class="rental-card rental-card-highlight"
            v-for="server in filteredServers"
            :key="server.id"
            :class="getStatusCardClass(server)"
          >
            <div class="rental-card-badges">
              <div>
                <span
                  v-if="server.highlight > 0"
                  :class="['badge', getHighlightClass(server.highlight)]"
                  >{{ getHighlightText(server.highlight) }}</span
                >
                <!-- 状态标签 -->
                <!-- <span
                  v-if="getServerStatus(server) === STATUS.pending"
                  class="badge status-badge status-badge-pending"
                  >⏳ {{ $t("serverList.btnWait") }}</span
                >
                <span
                  v-else-if="getServerStatus(server) === STATUS.preOrder"
                  class="badge status-badge status-badge-preorder"
                  >🔥 {{ $t("serverList.btnRent") }}</span
                >
                <span
                  v-else-if="getServerStatus(server) === STATUS.active"
                  class="badge status-badge status-badge-active"
                  >⚡ {{ $t("serverList.yunxz") }}</span
                > -->
              </div>
              <span style="color: #4b5056"
                >{{ $t("detail.code") }}{{ server.code }}</span
              >
              <div>
                <img
                  v-if="server.country_img"
                  :src="server.country_img"
                  :alt="$t('rental.countryAlt')"
                  class="country-flag"
                />
              </div>
            </div>

            <div class="-rental-card-header rental-card-gpu">
              <div class="rental-card-icon">
                <ServerIconIsometric size="sm" />
              </div>
              <div class="rental-card-title-wrap">
                <h3 class="rental-card-name">
                  {{ $t(server.code + ".s_model") }}
                </h3>
              </div>
            </div>

            <div class="rental-card-pricing">
              <div
                class="price-item price-item-left"
                v-if="isServerActive(server)"
              >
                <span class="price-label">{{
                  $t("serverList.dailyIncome")
                }}</span>
                <span class="price-value-item rental-price"
                  >${{ getTodayIncome(server) }}</span
                >
              </div>
              <div class="price-divider" v-if="isServerActive(server)"></div>
              <div class="price-item chart-item">
                <span class="price-label">{{ $t("rental.incomeTrend") }}</span>
                <div :ref="'chartRef' + server.id" class="chart-box"></div>
              </div>
            </div>

            <div class="rental-value-progress">
              <div class="progress-track">
                <div
                  class="progress-bar"
                  v-if="isServerActive(server)"
                  :style="`width: ${calcValueProgress(server)}%`"
                >
                  <span class="progress-glow"></span>

                  <span class="progress-box" v-if="isServerActive(server)">
                    {{ calcValueProgress(server) }}%
                  </span>
                </div>
              </div>
            </div>

            <div class="rental-activity-period">
              <div class="period-header">
                <span class="period-label">{{
                  $t("rental.activityPeriod")
                }}</span>
                <span class="period-date">
                  {{ formatDate(server.begin_time) }} ~
                  {{ formatDate(server.end_time) }}
                </span>
              </div>
              <div class="period-footer">
                <span class="period-remaining">
                  {{ $t("rental.remaining") }}
                  <strong>{{ calcRemainingDays(server.end_time) }}</strong>
                  {{ $t("rental.days") }}
                </span>
                <span
                  class="period-status"
                  :class="getServerStatusClass(server)"
                >
                  {{ getServerStatusText(server) }}
                </span>
              </div>
            </div>

            <div class="rental-card-specs">
              <div class="spec-item">
                <span class="spec-label">{{ $t("rental.memory") }}</span>
                <span class="spec-value">{{
                  truncateText($t(server.code + ".s_ram"), 18)
                }}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">{{ $t("rental.storage") }}</span>
                <span class="spec-value">{{
                  truncateText($t(server.code + ".s_ssd"), 18)
                }}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">{{ $t("rental.bandwidth") }}</span>
                <span class="spec-value">{{
                  truncateText($t(server.code + ".s_bandwidth"), 18)
                }}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">{{ $t("serverList.vram") }}</span>
                <span class="spec-value">{{
                  truncateText($t(server.code + ".s_vram"), 18)
                }}</span>
              </div>
            </div>

            <!-- ===== 等待预售开始 - 科技风悬浮覆盖层（与 home/ServerList.vue 保持一致） ===== -->
            <div
              v-if="getServerStatus(server) === STATUS.pending"
              class="pending-tech"
            >
              <!-- 悬浮倒计时条 -->
              <div class="countdown-tech-bar">
                <div style="margin-top: 62%">
                  <!-- 左侧：标签 + 倒计时 -->
                  <div class="tech-left">
                    <span class="tech-label">
                      <span class="tech-dot"></span>
                      {{ $t("serverList.countdown") }}
                    </span>
                    <span class="tech-digits">{{
                      formatCountdown(server.start_time)
                    }}</span>
                  </div>
                </div>
                <!-- 按钮 -->
                <button
                  class="btn-rental btn-tech"
                  :class="getServerStatusClass(server)"
                  @click="handleRental(server)"
                >
                  <span class="btn-tech-text">{{
                    getServerStatusBtn(server)
                  }}</span>
                </button>
              </div>
            </div>

            <!-- ===== 预售中（preOrder） ===== -->
            <div v-if="getServerStatus(server) === STATUS.preOrder">
              <button
                class="btn-rental"
                :class="getServerStatusClass(server)"
                @click="handleRental(server)"
              >
                {{ getServerStatusBtn(server) }}
              </button>
            </div>

            <!-- ===== 运行中（active） ===== -->
            <div v-if="getServerStatus(server) === STATUS.active">
              <button
                class="btn-rental"
                :class="getServerStatusClass(server)"
                @click="handleRental(server)"
              >
                {{ getServerStatusBtn(server) }}
              </button>
            </div>

            <!-- ===== 已结束（ended） ===== -->
            <div v-if="getServerStatus(server) === STATUS.ended">
              <button
                class="btn-rental"
                :class="getServerStatusClass(server)"
                @click="handleRental(server)"
                :disabled="false"
              >
                {{ getServerStatusBtn(server) }}
              </button>
            </div>
          </div>
        </div>

        <!-- ===== 列表视图 ===== -->
        <div v-else class="rental-list">
          <div
            class="rental-list-item"
            v-for="server in filteredServers"
            :key="server.id"
          >
            <div class="list-item-main">
              <div class="list-item-icon">
                <ServerIconIsometric size="sm" />
              </div>
              <div class="list-item-info">
                <div class="list-item-header">
                  <h4>{{ truncateText($t(server.code + ".s_model"), 80) }}</h4>
                  <span
                    v-if="server.highlight === 1 || server.highlight === 2"
                    class="badge badge-hot"
                    >{{ $t("rental.tagHot") }}</span
                  >
                </div>

                <div class="list-price">
                  <img
                    v-if="server.country_img"
                    :src="server.country_img"
                    :alt="$t('rental.countryAlt')"
                    class="country-flag"
                  />
                </div>
              </div>

              <div class="list-item-pricing">
                <div class="list-price">
                  <span class="list-price-label">{{ $t("rental.value") }}</span>
                  <span class="list-price-value"
                    >${{ formatValue(server.value) }}</span
                  >
                </div>
                <div class="list-price">
                  <span class="list-price-label">{{
                    $t("serverList.dailyIncome")
                  }}</span>
                  <span class="list-price-value"
                    >${{ getTodayIncome(server) }}</span
                  >
                </div>
                <div class="list-price" v-if="isServerActive(server)">
                  <span class="list-price-label">{{
                    $t("rental.progress")
                  }}</span>
                  <span class="list-price-value"
                    >{{ calcValueProgress(server) }}%</span
                  >
                </div>
              </div>
              <div class="list-item-status">
                <span
                  class="period-status"
                  :class="getServerStatusClass(server)"
                >
                  {{ getServerStatusText(server) }}
                </span>
              </div>

              <!-- 待预购 -->
              <div
                class="btcc"
                v-if="getServerStatus(server) === STATUS.pending"
              >
                <div class="countdown-box countdown-list">
                  <span class="countdown-label">{{
                    $t("rental.countdown")
                  }}</span>
                  <span class="countdown-time">{{
                    formatCountdown(server.start_time)
                  }}</span>
                </div>
                <button
                  class="btn btn-rental-list"
                  :class="getServerStatusClass(server)"
                  @click="handleRental(server)"
                >
                  {{ getServerStatusBtn(server) }}
                </button>
              </div>

              <!-- 预购中 -->
              <div
                class="btcc"
                v-if="getServerStatus(server) === STATUS.preOrder"
              >
                <button
                  class="btn btn-rental-list"
                  :class="getServerStatusClass(server)"
                  @click="handleRental(server)"
                >
                  {{ getServerStatusBtn(server) }}
                </button>
              </div>

              <!-- 运行中 -->
              <div
                class="btcc"
                v-if="getServerStatus(server) === STATUS.active"
              >
                <button
                  class="btn btn-rental-list"
                  :class="getServerStatusClass(server)"
                  @click="handleRental(server)"
                >
                  {{ getServerStatusBtn(server) }}
                </button>
              </div>

              <!-- 已结束 -->
              <div class="btcc" v-if="getServerStatus(server) === STATUS.ended">
                <button
                  class="btn-rental btn btn-rental-list"
                  :class="getServerStatusClass(server)"
                  @click="handleRental(server)"
                >
                  {{ getServerStatusBtn(server) }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- ===== 分页 ===== -->
        <div class="rental-pagination" v-if="totalServers > 0">
          <button
            class="page-btn"
            :disabled="currentPage === 1"
            @click="changePage(currentPage - 1)"
          >
            {{ $t("rental.prev") }}
          </button>
          <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
          <button
            class="page-btn"
            :disabled="currentPage === totalPages"
            @click="changePage(currentPage + 1)"
          >
            {{ $t("rental.next") }}
          </button>
          <select
            class="page-size-select"
            v-model="pageSize"
            @change="changePage(1)"
          >
            <option :value="6">{{ $t("rental.perPage", { count: 6 }) }}</option>
            <option :value="9">{{ $t("rental.perPage", { count: 9 }) }}</option>
            <option :value="12">
              {{ $t("rental.perPage", { count: 12 }) }}
            </option>
            <option :value="18">
              {{ $t("rental.perPage", { count: 18 }) }}
            </option>
          </select>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { loadProductsLanguage } from "@/locales";
import * as echarts from "echarts";
import ServerIconIsometric from "@/components/server/ServerIconIsometric.vue";

const STATUS = {
  pending: "pending",
  preOrder: "preOrder",
  active: "active",
  ended: "ended",
};

const ServerHighlightMap = {
  1: {
    textKey: "serverList.tagHot",
    class: "badge-hot",
  },
  2: {
    textKey: "serverList.tagRecommend",
    class: "badge-recommend",
  },
  3: {
    textKey: "serverList.tagNew",
    class: "badge-available",
  },
};

const ServerSpecMap = {
  1: "rental.spec1",
  2: "rental.spec2",
  3: "rental.spec3",
  4: "rental.spec4",
};

export default {
  name: "RentalPage",
  components: {
    ServerIconIsometric,
  },
  data() {
    return {
      STATUS,
      allServers: [],
      totalServers: 0,
      loading: false,
      currentPage: 1,
      pageSize: 9,
      searchKeyword: "",
      activeFilter: "all",
      sortBy: "default",

      viewMode: "grid",

      chartInstances: {},
      now: Date.now(),
      countdownTimer: null,
    };
  },
  computed: {
    filteredServers() {
      let result = [...this.allServers];

      if (this.searchKeyword.trim()) {
        const keyword = this.searchKeyword.toLowerCase();
        result = result.filter(
          (s) =>
            s.title?.toLowerCase().includes(keyword) ||
            s.gpu?.toLowerCase().includes(keyword) ||
            s.label?.toLowerCase().includes(keyword),
        );
      }

      switch (this.activeFilter) {
        case "hot":
          result = result.filter((s) => s.highlight === 1);
          break;
        case "recommend":
          result = result.filter((s) => s.highlight === 2);
          break;
        case "new":
          result = result.filter((s) => s.highlight === 3);
          break;
        case "spec-1":
          result = result.filter((s) => s.spec === 1);
          break;
        case "spec-2":
          result = result.filter((s) => s.spec === 2);
          break;
        case "spec-3":
          result = result.filter((s) => s.spec === 3);
          break;
        case "spec-4":
          result = result.filter((s) => s.spec === 4);
          break;
        case "pending":
          result = result.filter(
            (s) => this.getServerStatus(s) === STATUS.pending,
          );
          break;
        case "preOrder":
          result = result.filter(
            (s) => this.getServerStatus(s) === STATUS.preOrder,
          );
          break;
        case "active":
          result = result.filter(
            (s) => this.getServerStatus(s) === STATUS.active,
          );
          break;
        default:
          break;
      }

      switch (this.sortBy) {
        case "value-asc":
          result.sort((a, b) => (a.value || 0) - (b.value || 0));
          break;
        case "value-desc":
          result.sort((a, b) => (b.value || 0) - (a.value || 0));
          break;
        case "income-asc":
          result.sort(
            (a, b) =>
              (parseFloat(a.today_income) || 0) -
              (parseFloat(b.today_income) || 0),
          );
          break;
        case "income-desc":
          result.sort(
            (a, b) =>
              (parseFloat(b.today_income) || 0) -
              (parseFloat(a.today_income) || 0),
          );
          break;
        default:
          break;
      }

      return result;
    },
    totalPages() {
      return Math.ceil(this.totalServers / this.pageSize);
    },
  },
  watch: {
    "$i18n.locale": function (newLang, oldLang) {
      console.log(`语言从 ${oldLang} 切换到 ${newLang}`);
      this.loadProductTranslations();
    },
  },
  mounted() {
    this.getServerList();
    this.countdownTimer = setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  updated() {},
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    Object.values(this.chartInstances).forEach((chart) => {
      if (chart) chart.dispose();
    });
    this.chartInstances = {};
  },
  methods: {
    // ===== 截断文本 =====
    truncateText(text, maxLength) {
      if (!text) return "--";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    },
    async loadProductTranslations() {
      const codes = this.allServers.map((p) => p.code);
      await loadProductsLanguage(codes, {
        path: "server/",
        module: "server",
      });
    },
    // ===== 获取服务器列表 =====
    getServerList() {
      this.loading = true;
      this.$api.server
        .getServerList({
          page: this.currentPage,
          size: this.pageSize,
        })
        .then((res) => {
          if (res.code === 0 && res.data) {
            const data = res.data.data || [];
            this.allServers = data.map((item) => ({
              ...item,
              surplus:
                item.surplus || Math.floor(Math.random() * item.value * 0.7),
              dailyYield: (Math.random() * 2 + 0.3).toFixed(1) + "%",
            }));
            this.totalServers = res.data.total || data.length;
          } else {
            this.allServers = [];
            this.totalServers = 0;
          }

          this.loadProductTranslations();
        })
        .catch((err) => {
          console.error("获取服务器列表失败:", err);
          this.allServers = [];
          this.totalServers = 0;
        })
        .finally(() => {
          this.loading = false;
          this.$nextTick(() => {
            this.initCharts();
          });
        });
    },

    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.getServerList();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    handleSearch() {},
    handleFilter() {},
    handleSort() {},

    getSpecText(spec) {
      return this.$t(ServerSpecMap[spec] || "");
    },
    getHighlightText(highlight) {
      const key = ServerHighlightMap[highlight]?.textKey;
      return key ? this.$t(key) : "";
    },
    getHighlightClass(highlight) {
      return ServerHighlightMap[highlight]?.class || "";
    },
    // ===== 获取状态卡片样式类 =====
    getStatusCardClass(server) {
      const status = this.getServerStatus(server);
      if (status === STATUS.pending) return "card-status-pending";
      if (status === STATUS.preOrder) return "card-status-preorder";
      if (status === STATUS.active) return "card-status-active";
      return "";
    },

    formatDate(dateStr) {
      if (!dateStr) return "--";
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    formatValue(value) {
      if (!value) return "0";
      if (value >= 10000) {
        return (value / 10000).toFixed(1) + "w";
      }
      return value.toLocaleString();
    },

    calcRemainingDays(end) {
      if (!end) return 0;
      const now = new Date().getTime();
      const endTime = new Date(end).getTime();
      const diff = endTime - now;
      if (diff <= 0) return 0;
      return Math.ceil(diff / (1000 * 60 * 60 * 24));
    },

    calcValueProgress(item) {
      const rate = (1 - (item.surplus - item.sale)) * 100;
      if (!rate) return 0;
      return Math.min(Math.round(rate), 100);
    },
    calcValueAmount(item) {
      const amount = (item.surplus - item.sale) * item.value;
      if (!amount) return 0;
      return Math.max(Math.round(amount), 0);
    },

    // getPeriodStatus(server) {
    //   const start = server.start_time;
    //   const begin = server.begin_time;
    //   const end = server.end_time;

    //   //等待预购中
    //   if (!start || !begin || !end) {
    //     return STATUS.pending
    //   }

    //   const now = new Date().getTime();
    //   const startTime = new Date(start).getTime();
    //   const beginTime = new Date(begin).getTime();
    //   const endTime = new Date(end).getTime();

    //   //等待预购中
    //   if (now < startTime) {
    //     return STATUS.pending
    //   }

    //   //预购中
    //   if (startTime < now && now < beginTime) {
    //     return STATUS.preOrder
    //   }

    //   //运行中
    //   if (beginTime < now && now < endTime) {
    //     return STATUS.active
    //   }

    //   //已结束
    //   return STATUS.ended;
    // },

    // getPeriodStatusText(start, end) {
    //   const status = this.getPeriodStatus(start, end);
    //   const map = {
    //     active: this.$t("rental.statusActive"),
    //     pending: this.$t("rental.statusPending"),
    //     ended: this.$t("rental.statusEnded"),
    //   };
    //   return map[status] || this.$t("rental.statusActive");
    // },

    // ===== 获取今日收益 =====
    getTodayIncome(server) {
      if (this.getServerStatus(server) === STATUS.pending) {
        return "0.00";
      }
      return server.today_income || "0.00";
    },

    // ===== 判断服务器状态 =====
    getServerStatus(server) {
      const now = this.$dayjs();
      const startTime = server.start_time
        ? this.$dayjs(server.start_time)
        : null;
      const beginTime = server.begin_time
        ? this.$dayjs(server.begin_time)
        : null;
      const endTime = server.end_time ? this.$dayjs(server.end_time) : null;

      if (!startTime && !beginTime) {
        return server.status == 2 ? STATUS.active : STATUS.ended;
      }

      if (startTime && now.isBefore(startTime)) {
        return STATUS.pending;
      }

      if (beginTime && now.isBefore(beginTime)) {
        return STATUS.preOrder;
      }

      if (endTime && now.isAfter(endTime)) {
        return STATUS.ended;
      }

      return STATUS.active;
    },
    //状态
    isServerActive(server) {
      return this.getServerStatus(server) !== STATUS.pending;
    },
    //返回class
    getServerStatusClass(server) {
      // 等待预购中
      if (this.getServerStatus(server) === STATUS.pending) {
        return STATUS.pending;
      }

      // 预购中
      if (
        this.getServerStatus(server) === STATUS.preOrder &&
        this.calcValueProgress(server) < 100
      ) {
        return STATUS.preOrder;
      }

      // 运行中
      if (this.getServerStatus(server) === STATUS.active) {
        return STATUS.active;
      }

      return STATUS.ended;
    },
    //返回状态文字
    getServerStatusText(server) {
      //已结束
      if (this.getServerStatus(server) === STATUS.ended) {
        return this.$t("serverList.btnEnded");
      }

      //等待预购中
      if (this.getServerStatus(server) === STATUS.pending) {
        return this.$t("serverList.btnWait");
      }

      //运行中
      if (this.getServerStatus(server) === STATUS.active) {
        return this.$t("serverList.yunxz");
      }

      //已满额
      if (this.calcValueProgress(server) >= 100) {
        return this.$t("serverList.full");
      }

      //预购中
      if (this.getServerStatus(server) === STATUS.preOrder) {
        return this.$t("serverList.available");
      }

      //已满额
      return this.$t("serverList.full");
    },

    //返回状态文字
    getServerStatusBtn(server) {
      //已结束
      if (this.getServerStatus(server) === STATUS.ended) {
        return this.$t("serverList.btnEnded");
      }

      //等待预购中
      if (this.getServerStatus(server) === STATUS.pending) {
        return this.$t("serverList.look");
      }

      //运行中
      if (this.getServerStatus(server) === STATUS.active) {
        return this.$t("serverList.yunxz");
      }

      //已满额
      if (this.calcValueProgress(server) >= 100) {
        return this.$t("serverList.full");
      }

      //预购中
      if (this.getServerStatus(server) === STATUS.preOrder) {
        return this.$t("serverList.btnRent");
      }

      //已满额
      return this.$t("serverList.full");
    },
    // ===== 格式化倒计时 =====
    formatCountdown(time) {
      if (!time) return "";

      const targetTime = this.$dayjs(time);
      if (!targetTime.isValid()) return "";

      const diff = targetTime.diff(this.$dayjs(this.now));
      if (diff <= 0) return "00:00:00";

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      const pad = (n) => String(n).padStart(2, "0");

      if (days > 0) {
        return `${days}${this.$t("serverList.days")} ${pad(hours)}:${pad(
          minutes,
        )}:${pad(seconds)}`;
      }
      return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    },

    initCharts() {
      this.filteredServers.forEach((server) => {
        const refKey = `chartRef${server.id}`;
        const dom = this.$refs[refKey];
        if (!dom) return;

        if (this.chartInstances[server.id]) {
          this.chartInstances[server.id].dispose();
          delete this.chartInstances[server.id];
        }

        const chartDom = Array.isArray(dom) ? dom[0] : dom;
        if (!chartDom) return;

        const chart = echarts.init(chartDom);
        this.chartInstances[server.id] = chart;

        const trendData = [];
        const xData = [];
        const incomeData = server.income;

        if (this.getServerStatus(server) !== STATUS.active) {
          const today = new Date();
          for (let i = 6; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(today.getDate() - i);
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            xData.push(`${month}-${day}`);
            trendData.push(0);
          }
        } else if (Array.isArray(incomeData) && incomeData.length > 0) {
          incomeData.forEach((item) => {
            xData.push(item.date ? item.date.slice(5) : "--");
            trendData.push(parseFloat(item.amount) || 0);
          });
        } else {
          const today = new Date();
          for (let i = 6; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(today.getDate() - i);
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            xData.push(`${month}-${day}`);
            trendData.push(0);
          }
        }

        const dataMin = Math.min(...trendData);
        const dataMax = Math.max(...trendData);
        const dataRange = dataMax - dataMin;
        const yMin = dataRange > 0 ? dataMin - dataRange * 0.1 : dataMin - 1;
        const yMax = dataRange > 0 ? dataMax + dataRange * 0.1 : dataMax + 1;

        const option = {
          tooltip: {
            trigger: "axis",
            formatter: (params) => {
              const p = params[0];
              return `${p.axisValue}<br/>${this.$t(
                "rental.income",
              )}: <strong>$${p.value.toFixed(0)}</strong>`;
            },
            backgroundColor: "rgba(10, 14, 26, 0.85)",
            borderColor: "rgba(0, 180, 255, 0.2)",
            borderWidth: 1,
            textStyle: { color: "#fff", fontSize: 11 },
          },
          grid: {
            left: 2,
            right: 2,
            top: 0,
            bottom: 0,
          },
          xAxis: {
            type: "category",
            data: xData,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
              color: "rgba(255,255,255,0.2)",
              fontSize: 9,
              interval: 0,
            },
          },
          yAxis: {
            type: "value",
            min: yMin,
            max: yMax,
            splitLine: { show: false },
            axisLabel: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
          },
          series: [
            {
              type: "line",
              smooth: true,
              data: trendData,
              symbol: "circle",
              symbolSize: 4,
              lineStyle: {
                color: "#00b4ff",
                width: 2,
              },
              areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: "rgba(0, 180, 255, 0.3)" },
                  { offset: 1, color: "rgba(0, 180, 255, 0.02)" },
                ]),
              },
              itemStyle: {
                color: "#00b4ff",
              },
            },
          ],
        };

        chart.setOption(option, true);
      });
    },

    handleRental(server) {
      this.$router.push({
        name: "RentalDetail",
        query: { code: server.code },
      });
    },
  },
};
</script>

<style scoped>
/* ===== 基础重置 & 全局 ===== */
.rental-page {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  background: #0a0e1a;
  color: #fff;
  overflow-x: hidden;
}

/* ===== 英雄区 ===== */
.rental-hero {
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 60px 20px 50px;
  background: linear-gradient(135deg, #0a0e1a, #0d1a2b);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.rental-hero-overlay {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse at 70% 30%,
    rgba(0, 180, 255, 0.05),
    transparent 70%
  );
  pointer-events: none;
}

.rental-hero-content {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 700px;
}

.rental-hero-title {
  font-size: 48px;
  font-weight: 900;
  margin: 0 0 12px 0;
  line-height: 1.1;
}

.rental-hero-desc {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.8;
  margin: 0 0 28px 0;
}

.highlight {
  background: linear-gradient(135deg, #00b4ff, #7c3aed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.rental-hero-stats {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 32px;
}

.hero-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.hero-stat-number {
  font-size: 28px;
  font-weight: 800;
  color: #fff;
}

.hero-stat-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
}

.hero-stat-divider {
  width: 1px;
  height: 36px;
  background: rgba(255, 255, 255, 0.06);
}

.section-header {
  text-align: center;
  margin-bottom: 48px;
}

.section-badge {
  display: inline-block;
  padding: 4px 16px;
  border-radius: 20px;
  background: rgba(0, 180, 255, 0.08);
  border: 1px solid rgba(0, 180, 255, 0.1);
  font-size: 13px;
  color: #00b4ff;
  margin-bottom: 12px;
}

.section-title {
  font-size: 40px;
  font-weight: 800;
  margin: 0;
}

/* ===== 筛选栏 ===== */
.rental-filter-section {
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(20px);
}

.rental-filter-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.filter-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  flex: 1;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 8px 16px;
  min-width: 200px;
  flex: 0 1 260px;
  color: rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.search-box:focus-within {
  border-color: rgba(0, 180, 255, 0.3);
  box-shadow: 0 0 0 4px rgba(0, 100, 255, 0.06);
  color: #00b4ff;
}

.search-input {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 14px;
  outline: none;
  width: 100%;
  font-family: inherit;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.2);
}

.filter-tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.filter-tag {
  padding: 5px 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  white-space: nowrap;
}

.filter-tag:hover {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.12);
}

.filter-tag.active {
  background: rgba(0, 180, 255, 0.12);
  border-color: rgba(0, 180, 255, 0.2);
  color: #00b4ff;
}

.filter-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.sort-select {
  padding: 7px 14px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  outline: none;
  cursor: pointer;
  font-family: inherit;
  min-width: 100px;
}

.sort-select option {
  background: #1a2332;
  color: #fff;
}

.view-toggle {
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  padding: 4px;
}

.view-btn {
  padding: 6px 10px;
  border-radius: 6px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
}

.view-btn:hover {
  color: rgba(255, 255, 255, 0.6);
}

.view-btn.active {
  background: rgba(0, 180, 255, 0.12);
  color: #00b4ff;
}

/* ===== 服务器列表 ===== */
.rental-list-section {
  padding: 24px 20px 60px;
  max-width: 1200px;
  margin: 0 auto;
}

.rental-result-count {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.3);
  margin-bottom: 20px;
}

.rental-result-count span {
  color: #fff;
  font-weight: 600;
}

/* ===== 加载 & 空状态 ===== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 16px;
  color: rgba(255, 255, 255, 0.3);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.06);
  border-top-color: #00b4ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 12px;
  color: rgba(255, 255, 255, 0.3);
}

.empty-icon {
  font-size: 48px;
}

/* ===== 网格视图 ===== */
.rental-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.rental-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 18px 16px;
  transition: all 0.4s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.rental-card:hover {
  transform: translateY(-6px);
  border-color: rgba(0, 180, 255, 0.15);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  background: rgba(255, 255, 255, 0.06);
}

.rental-card-highlight {
  border-color: rgba(255, 215, 0, 0.12);
  background: linear-gradient(
    135deg,
    rgba(255, 215, 0, 0.03),
    rgba(0, 180, 255, 0.03)
  );
}

.rental-card-highlight:hover {
  border-color: rgba(255, 215, 0, 0.25);
}

/* ============================================================
   三种状态卡片差异化设计
   ============================================================ */

/* ===== 等待预售开始 - 蓝色科技风 ===== */
.card-status-pending {
  border-color: rgba(0, 180, 255, 0.25);
  background: linear-gradient(
    160deg,
    rgba(0, 180, 255, 0.06),
    rgba(0, 10, 30, 0.4) 60%
  );
  box-shadow: 0 0 30px rgba(0, 180, 255, 0.08),
    inset 0 1px 0 rgba(0, 180, 255, 0.08);
}

.card-status-pending:hover {
  border-color: rgba(0, 180, 255, 0.4);
  box-shadow: 0 8px 40px rgba(0, 180, 255, 0.15),
    inset 0 1px 0 rgba(0, 180, 255, 0.1);
}

/* 顶部蓝色光带 */
.card-status-pending::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 180, 255, 0.6),
    transparent
  );
  border-radius: 16px 16px 0 0;
  animation: status-pending-glow 2.5s ease-in-out infinite;
}

@keyframes status-pending-glow {
  0%,
  100% {
    opacity: 0.4;
  }

  50% {
    opacity: 1;
  }
}

/* ===== 预售中 - 橙色/金色系 ===== */
.card-status-preorder {
  border-color: rgba(255, 152, 0, 0.3);
  background: linear-gradient(
    160deg,
    rgba(255, 152, 0, 0.07),
    rgba(30, 20, 0, 0.4) 60%
  );
  box-shadow: 0 0 30px rgba(255, 152, 0, 0.1),
    inset 0 1px 0 rgba(255, 152, 0, 0.08);
}

.card-status-preorder:hover {
  border-color: rgba(255, 152, 0, 0.45);
  box-shadow: 0 8px 40px rgba(255, 152, 0, 0.18),
    inset 0 1px 0 rgba(255, 152, 0, 0.1);
}

/* 顶部橙色光带 */
.card-status-preorder::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 152, 0, 0.7),
    transparent
  );
  border-radius: 16px 16px 0 0;
  animation: status-preorder-glow 2s ease-in-out infinite;
}

@keyframes status-preorder-glow {
  0%,
  100% {
    opacity: 0.5;
  }

  50% {
    opacity: 1;
  }
}

/* ===== 运行中 - 绿色系 ===== */
.card-status-active {
  border-color: rgba(0, 201, 81, 0.25);
  background: linear-gradient(
    160deg,
    rgba(0, 201, 81, 0.05),
    rgba(0, 20, 10, 0.4) 60%
  );
  box-shadow: 0 0 30px rgba(0, 201, 81, 0.08),
    inset 0 1px 0 rgba(0, 201, 81, 0.08);
}

.card-status-active:hover {
  border-color: rgba(0, 201, 81, 0.4);
  box-shadow: 0 8px 40px rgba(0, 201, 81, 0.15),
    inset 0 1px 0 rgba(0, 201, 81, 0.1);
}

/* 顶部绿色光带 */
.card-status-active::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 201, 81, 0.6),
    transparent
  );
  border-radius: 16px 16px 0 0;
  animation: status-active-glow 3s ease-in-out infinite;
}

@keyframes status-active-glow {
  0%,
  100% {
    opacity: 0.4;
  }

  50% {
    opacity: 1;
  }
}

/* ===== 状态标签 ===== */
.status-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 8px;
  letter-spacing: 0.3px;
}

.status-badge-pending {
  background: rgba(0, 180, 255, 0.12);
  color: #00b4ff;
  border: 1px solid rgba(0, 180, 255, 0.2);
}

.status-badge-preorder {
  background: rgba(255, 152, 0, 0.12);
  color: #ff9800;
  border: 1px solid rgba(255, 152, 0, 0.25);
  animation: preorder-pulse 2s ease-in-out infinite;
}

@keyframes preorder-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 rgba(255, 152, 0, 0);
  }

  50% {
    box-shadow: 0 0 12px rgba(255, 152, 0, 0.2);
  }
}

.status-badge-active {
  background: rgba(0, 201, 81, 0.12);
  color: #00c951;
  border: 1px solid rgba(0, 201, 81, 0.2);
}

/* ===== 标签 ===== */

.rental-card-badges {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
  flex-wrap: wrap;
  justify-content: space-between;
}

.badge {
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.badge-hot {
  background: rgba(255, 152, 0, 0.15);
  color: #ff9800;
}

.badge-available {
  background: rgba(0, 201, 81, 0.12);
  color: #00c951;
}

.badge-sold {
  background: rgba(255, 107, 107, 0.12);
  color: #ff6b6b;
}

.badge-recommend {
  background: rgb(255 235 59 / 11%);
  color: #ffeb3b;
}

.country-flag {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

/* ===== 头部 ===== */
.rental-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  min-width: 0;
}

.rental-card-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(0, 180, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00b4ff;
  flex-shrink: 0;
  overflow: hidden;
}

.rental-card-title-wrap {
  min-width: 0;
  flex: 1;
}

.rental-card-name {
  font-size: 17px;
  font-weight: 700;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rental-card-spec {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
  display: block;
}

.country-name {
  font-size: 15px !important;
}

/* ===== GPU ===== */
.rental-card-gpu {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 12px;
  border: 1px solid rgba(255, 255, 255, 0.04);
  min-width: 0;
  display: flex;
  gap: 15px;
  align-items: center;
}

.gpu-model {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gpu-detail {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ===== 价格 ===== */
.rental-card-pricing {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  margin-bottom: 12px;
}

.price-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  flex-shrink: 1;
  min-width: 0;
}

.price-item-left {
  flex: 0 0 30%;
}

.price-item:last-child {
  flex: 1;
  min-width: 0;
}

.price-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.25);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.price-value-item {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
}

.rental-price {
  color: #ffd866;
  font-size: 17px;
}

.price-divider {
  width: 1px;
  height: 28px;
  background: rgba(255, 255, 255, 0.04);
  flex-shrink: 0;
}

.chart-item {
  flex: 1;
  min-width: 0;
}

.chart-box {
  width: 100%;
  height: 90px;
  min-width: 50px;
}

/* ===== 价值进度条 ===== */

.rental-value-progress {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.value-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.value-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.value-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.value-progress-text {
  font-size: 14px;
  font-weight: 700;
  background: linear-gradient(135deg, #00b4ff, #7c3aed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.progress-track {
  position: relative;
  width: 100%;
  height: 15px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  overflow: visible;
}

.progress-bar {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #00b4ff, #7c3aed);
  position: relative;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.progress-glow {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  background: radial-gradient(circle, rgba(0, 180, 255, 0.4), transparent 70%);
  border-radius: 50%;
  animation: glow-pulse 2s ease-in-out infinite;
}

@keyframes glow-pulse {
  0%,
  100% {
    opacity: 0.6;
    transform: translateY(-50%) scale(1);
  }

  50% {
    opacity: 1;
    transform: translateY(-50%) scale(1.3);
  }
}

.progress-box {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}

.value-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.03);
}

.value-remaining {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
}

.value-remaining strong {
  color: #ffd866;
  font-weight: 700;
}

.value-status {
  font-size: 13px;
  font-weight: 600;
  padding: 2px 12px;
  border-radius: 10px;
  white-space: nowrap;
}

.value-status.high {
  background: rgba(255, 107, 107, 0.12);
  color: #ff6b6b;
}

.value-status.medium {
  background: rgba(255, 152, 0, 0.12);
  color: #ff9800;
}

.value-status.low {
  background: rgba(0, 180, 255, 0.12);
  color: #00b4ff;
}

.value-status.pending {
  background: rgba(0, 201, 81, 0.12);
  color: #00c951;
}

/* ===== 活动周期 ===== */
.rental-activity-period {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.period-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: nowrap;
  min-width: 0;
}

.period-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  flex-shrink: 0;
}

.period-date {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
  font-family: "SF Mono", "Courier New", monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.period-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.03);
}

.period-remaining {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
}

.period-remaining strong {
  color: #ffd866;
  font-weight: 700;
}

.period-status {
  font-size: 13px;
  font-weight: 600;
  padding: 2px 12px;
  border-radius: 10px;
  white-space: nowrap;
}

.period-status.pending {
  background: rgb(195 195 195 / 8%);
  color: #999999;
}

.period-status.preOrder {
  background: rgba(0, 201, 81, 0.08);
  color: #00c951;
}

.period-status.active {
  background: rgba(0, 201, 81, 0.08);
  color: #00c951;
}

.period-status.ended {
  background: rgba(255, 107, 107, 0.08);
  color: #ff6b6b;
}

/* ===== 规格 ===== */
.rental-card-specs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px 10px;
  margin-bottom: 12px;
}

.spec-item {
  display: flex;
  justify-content: space-between;
  padding: 2px 0;
  min-width: 0;
}

.spec-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.25);
  flex-shrink: 0;
}

.spec-value {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80px;
  text-align: right;
}

.available-yes {
  color: #00c951;
}

.available-blue {
  color: #0068ff;
}

.available-no {
  color: #ff6b6b;
}

/* ===== 预售中 - 状态文字颜色（橙色） ===== */
.card-status-preorder .spec-value.available-yes {
  color: #ff9800;
}

/* ===== 网格视图按钮 ===== */
.btn-rental {
  width: 100%;
  padding: 10px 0;
  border-radius: 10px;
  background: linear-gradient(135deg, #00b4ff, #0066ff);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: auto;
}

/* ===== 预售中 - 倒计时样式 ===== */
.card-status-preorder .countdown-box {
  background: rgba(255, 152, 0, 0.1);
  border-color: rgba(255, 152, 0, 0.2);
}

.card-status-preorder .countdown-label {
  color: rgba(255, 152, 0, 0.8);
}

.card-status-preorder .countdown-time {
  color: #ff9800;
}

/* ===== 运行中 - 倒计时样式 ===== */
.card-status-active .countdown-box {
  background: rgba(0, 201, 81, 0.08);
  border-color: rgba(0, 201, 81, 0.15);
}

.card-status-active .countdown-label {
  color: rgba(0, 201, 81, 0.7);
}

.card-status-active .countdown-time {
  color: #00c951;
}

/* ===== 预售中 - 进度条样式 ===== */
.card-status-preorder .progress-track {
  background: rgba(255, 152, 0, 0.08);
}

.card-status-preorder .progress-bar {
  background: linear-gradient(90deg, #ff9800, #ff6d00);
}

/* ===== 运行中 - 进度条样式 ===== */
.card-status-active .progress-track {
  background: rgba(0, 201, 81, 0.08);
}

.card-status-active .progress-bar {
  background: linear-gradient(90deg, #00c951, #00a843);
}

.btn-rental:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 100, 255, 0.3);
}

.btn-rental:disabled {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.2);
  cursor: not-allowed;
}

.btn-rental.btn-full {
  background: rgba(255, 107, 107, 0.12);
  color: #ff6b6b;
  border: 1px solid rgba(255, 107, 107, 0.2);
  cursor: not-allowed;
}

.btn-arrow {
  transition: transform 0.3s ease;
}

.btn-rental:hover:not(:disabled) .btn-arrow {
  transform: translateX(4px);
}

/* ===== 倒计时 ===== */
.countdown-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  margin-top: 10px;
  border-radius: 10px;
  background: rgba(255, 152, 0, 0.08);
  border: 1px solid rgba(255, 152, 0, 0.15);
}

.countdown-label {
  font-size: 12px;
  color: rgba(255, 152, 0, 0.7);
  font-weight: 500;
  white-space: nowrap;
}

.countdown-time {
  font-size: 15px;
  font-weight: 800;
  color: #ff9800;
  font-family: "SF Mono", "Courier New", monospace;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

/* 列表视图倒计时 */
.countdown-list {
  margin-bottom: 0;
  margin-top: 0;
  padding: 4px 10px;
  flex-shrink: 0;
}

.countdown-list .countdown-time {
  font-size: 13px;
}

/* ============================================================
   等待预售开始 - 科技风倒计时（与 home/ServerList.vue 保持一致）
   ============================================================ */

.pending-tech {
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
  z-index: 10;
  position: absolute;
  top: 0;
  width: 100%;
  left: 0;
  height: 100%;
  padding: 0;
  pointer-events: none;
}

.pending-tech .countdown-tech-bar {
  pointer-events: auto;
}

/* 状态标签在 pending 覆盖层之上 */
.rental-card .rental-card-badges {
  position: relative;
  z-index: 10;
}

/* ===== 倒计时条 ===== */
.countdown-tech-bar {
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;
  background: rgba(0, 10, 30, 0.6);
  /* backdrop-filter: blur(16px); */
  /* -webkit-backdrop-filter: blur(16px); */
  border: 1px solid rgba(0, 180, 255, 0.12);
  border-radius: 12px;
  padding: 10px 16px 10px 18px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(0, 180, 255, 0.05);
  height: 100%;
}

/* 边框光效 */
.countdown-tech-bar::before {
  content: "";
  position: absolute;
  inset: -1px;
  border-radius: 12px;
  padding: 1px;
  background: linear-gradient(
    135deg,
    rgba(0, 180, 255, 0.15),
    transparent 40%,
    rgba(124, 58, 237, 0.1)
  );
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}

/* 顶部扫描线 */
.countdown-tech-bar::after {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 60%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 180, 255, 0.3),
    transparent
  );
  animation: scan-line 4s ease-in-out infinite;
}

@keyframes scan-line {
  0% {
    left: -100%;
  }

  50% {
    left: 100%;
  }

  100% {
    left: 100%;
  }
}

/* 底部微光 */
.countdown-tech-bar .tech-glow {
  position: absolute;
  bottom: -20px;
  right: 20px;
  width: 80px;
  height: 80px;
  background: radial-gradient(circle, rgba(0, 180, 255, 0.04), transparent 70%);
  pointer-events: none;
}

/* ===== 左侧:标签+倒计时 ===== */
.tech-left {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  width: 100%;
  position: relative;
  z-index: 1;
}

.tech-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 500;
  color: rgba(0, 180, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 1.5px;
  white-space: nowrap;
  flex-shrink: 0;
}

.tech-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #00b4ff;
  box-shadow: 0 0 12px rgba(0, 180, 255, 0.3);
  animation: dot-pulse 1.5s ease-in-out infinite;
}

@keyframes dot-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.3;
    transform: scale(0.7);
  }
}

.tech-digits {
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  font-family: "SF Mono", "JetBrains Mono", "Courier New", monospace;
  letter-spacing: 2px;
  background: linear-gradient(90deg, #fff, rgba(0, 180, 255, 0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 0 40px rgba(0, 180, 255, 0.05);
}

/* ===== 科技风按钮 ===== */
.btn-rental.btn-tech {
  background: #0068ff;
  color: rgb(255 255 255);
  border: 1px solid rgba(0, 180, 255, 0.06);
  border-radius: 10px;
  padding: 10px 0;
  font-size: 14px;
  font-weight: 500;
  cursor: default;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  position: relative;
  overflow: hidden;
  margin-bottom: 6px;
  width: 100%;
}

.btn-tech-text {
  opacity: 0.8;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

/* 满额状态 */
.btn-rental.btn-tech.btn-full {
  background: rgba(255, 107, 107, 0.04);
  border-color: rgba(255, 107, 107, 0.06);
  color: rgba(255, 107, 107, 0.3);
}

/* ===== 列表视图 ===== */
.rental-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rental-list-item {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  padding: 14px 18px;
  transition: all 0.3s ease;
}

.rental-list-item:hover {
  border-color: rgba(0, 180, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
}

.list-item-main {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: nowrap;
}

.list-item-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(0, 180, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00b4ff;
  flex-shrink: 0;
  overflow: hidden;
}

.list-item-info {
  flex: 1;
  min-width: 0;

  display: flex;
  align-items: center;
  gap: 10px;
}

.list-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.list-item-header h4 {
  font-size: 15px;
  font-weight: 700;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.list-item-spec {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
}

.list-item-gpu {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}

.list-item-pricing {
  display: flex;
  gap: 20px;
  flex-shrink: 0;
}

.list-price {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.list-price-label {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.25);
  text-transform: uppercase;
}

.list-price-value {
  font-size: 14px;
  font-weight: 700;
  color: #fff;
}

.list-item-status {
  flex-shrink: 0;
}

.status-available {
  color: #00c951;
  font-size: 14px;
  font-weight: 600;
}

.status-unavailable {
  color: #ff6b6b;
  font-size: 14px;
  font-weight: 600;
}

.btn-rental-list {
  padding: 7px 20px;
  border-radius: 8px;
  background: linear-gradient(135deg, #00b4ff, #0066ff);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  flex-shrink: 0;
}

.btn-rental-list:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(0, 100, 255, 0.25);
}

.btn-rental-list:disabled {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.2);
  cursor: not-allowed;
}

.btn-rental-list.btn-full {
  background: rgba(255, 107, 107, 0.12);
  color: #ff6b6b;
  border: 1px solid rgba(255, 107, 107, 0.2);
  cursor: not-allowed;
}

/* ===== 分页 ===== */
.rental-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 32px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  flex-wrap: wrap;
}

.page-btn {
  padding: 8px 18px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.page-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.3);
}

.page-size-select {
  padding: 7px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  outline: none;
  cursor: pointer;
  font-family: inherit;
}

.page-size-select option {
  background: #1a2332;
  color: #fff;
}

/* ===== 响应式 ===== */
@media (max-width: 1200px) {
  .rental-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 992px) {
  .rental-filter-container {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-left {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    flex: unset;
    width: 100%;
  }

  .filter-tags {
    justify-content: center;
  }

  .filter-right {
    justify-content: center;
  }

  .rental-hero-title {
    font-size: 36px;
  }

  .list-item-gpu {
    max-width: 200px;
  }
}

@media (max-width: 768px) {
  .rental-grid {
    grid-template-columns: 1fr;
    max-width: 420px;
    margin: 0 auto;
  }

  .pending-tech {
    margin-top: -14px;
  }

  .countdown-tech-bar {
    padding: 8px 12px 8px 14px;
    border-radius: 10px;
  }

  .tech-left {
    gap: 10px;
  }

  .tech-label {
    font-size: 9px;
    letter-spacing: 1px;
  }

  .tech-dot {
    width: 5px;
    height: 5px;
  }

  .tech-digits {
    font-size: 18px;
    letter-spacing: 1.5px;
  }

  .btn-rental.btn-tech {
    padding: 8px 0;
    font-size: 13px;
  }

  .list-item-main {
    flex-wrap: wrap;
    gap: 10px;
  }

  .list-item-pricing {
    gap: 14px;
  }

  .list-price-value {
    font-size: 14px;
  }

  .rental-hero-stats {
    gap: 16px;
    flex-wrap: wrap;
  }

  .hero-stat-divider {
    display: none;
  }

  .section-title {
    font-size: 28px;
  }

  .rental-card-pricing {
    flex-wrap: wrap;
    gap: 6px;
  }

  .price-divider {
    display: none;
  }

  .chart-box {
    height: 60px;
  }

  .list-item-header h4 {
    max-width: 100px;
  }

  .list-item-gpu {
    max-width: 150px;
  }
}

@media (max-width: 480px) {
  .rental-hero-title {
    font-size: 28px;
  }

  .rental-hero-desc {
    font-size: 15px;
  }

  .rental-card {
    padding: 16px 14px;
  }

  .rental-card-specs {
    grid-template-columns: 1fr;
  }

  .rental-filter-section {
    padding: 10px 12px;
  }

  .filter-tag {
    font-size: 12px;
    padding: 4px 10px;
  }

  .chart-box {
    height: 50px;
    min-width: 50px;
  }

  .list-item-gpu {
    max-width: 120px;
  }

  .spec-value {
    max-width: 60px;
  }

  .period-date {
    font-size: 10px;
  }
}

/* ============================================================
   科技蓝统一配色（覆盖重载）
   - STATUS.pending  → 灰色（等待中，低调处理）
   - STATUS.preOrder → 品牌蓝（预售中）
   - STATUS.active   → 品牌蓝（运行中）
   去除过亮的橙/绿/黄，统一为冷色科技蓝
   ============================================================ */

/* ---------- 推荐卡片边框：金色 → 蓝色 ---------- */
.rental-card-highlight {
  border-color: rgba(96, 165, 250, 0.18);
  background: linear-gradient(
    135deg,
    rgba(96, 165, 250, 0.04),
    rgba(0, 180, 255, 0.04)
  );
}

.rental-card-highlight:hover {
  border-color: rgba(96, 165, 250, 0.3);
}

/* ==================== pending = 灰色 ==================== */
.card-status-pending {
  border-color: rgba(148, 163, 184, 0.22);
  background: linear-gradient(
    160deg,
    rgba(148, 163, 184, 0.05),
    rgba(15, 20, 30, 0.4) 60%
  );
  box-shadow: 0 0 30px rgba(148, 163, 184, 0.05),
    inset 0 1px 0 rgba(148, 163, 184, 0.06);
}

.card-status-pending:hover {
  border-color: rgba(148, 163, 184, 0.35);
  box-shadow: 0 8px 40px rgba(148, 163, 184, 0.1),
    inset 0 1px 0 rgba(148, 163, 184, 0.08);
}

.card-status-pending::before {
  background: linear-gradient(
    90deg,
    transparent,
    rgba(148, 163, 184, 0.45),
    transparent
  );
}

/* pending 状态文字：蓝色 → 灰色 */
.available-blue {
  color: #8b95a5;
}

/* pending 倒计时覆盖层 → 灰色 */
.countdown-tech-bar {
  border-color: rgba(148, 163, 184, 0.14);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(148, 163, 184, 0.04);
}

.countdown-tech-bar::before {
  background: linear-gradient(
    135deg,
    rgba(148, 163, 184, 0.12),
    transparent 40%,
    rgba(148, 163, 184, 0.08)
  );
}

.countdown-tech-bar::after {
  background: linear-gradient(
    90deg,
    transparent,
    rgba(148, 163, 184, 0.25),
    transparent
  );
}

.tech-label {
  color: rgba(148, 163, 184, 0.6);
}

.tech-dot {
  background: #8b95a5;
  box-shadow: 0 0 12px rgba(148, 163, 184, 0.25);
}

.tech-digits {
  background: linear-gradient(90deg, #fff, rgba(148, 163, 184, 0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* pending 科技按钮 → 灰色 */
.btn-rental.btn-tech {
  background: linear-gradient(135deg, #6b7280, #4b5563);
}

/* pending 倒计时框 → 灰色 */
.countdown-box,
.card-status-preorder .countdown-box {
  background: rgba(148, 163, 184, 0.06);
  border-color: rgba(148, 163, 184, 0.12);
}

.countdown-label,
.card-status-preorder .countdown-label {
  color: rgba(148, 163, 184, 0.6);
}

.countdown-time,
.card-status-preorder .countdown-time {
  color: #94a3b8;
}

/* ============ preOrder & active = 品牌蓝 ============ */
.card-status-preorder,
.card-status-active {
  border-color: rgba(0, 180, 255, 0.28);
  background: linear-gradient(
    160deg,
    rgba(0, 180, 255, 0.06),
    rgba(0, 10, 30, 0.4) 60%
  );
  box-shadow: 0 0 30px rgba(0, 180, 255, 0.08),
    inset 0 1px 0 rgba(0, 180, 255, 0.08);
}

.card-status-preorder:hover,
.card-status-active:hover {
  border-color: rgba(0, 180, 255, 0.42);
  box-shadow: 0 8px 40px rgba(0, 180, 255, 0.15),
    inset 0 1px 0 rgba(0, 180, 255, 0.1);
}

.card-status-preorder::before,
.card-status-active::before {
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 180, 255, 0.6),
    transparent
  );
}

/* ---------- 状态标签 ---------- */
.status-badge-pending {
  background: rgba(148, 163, 184, 0.1);
  color: #94a3b8;
  border-color: rgba(148, 163, 184, 0.18);
}

.status-badge-preorder,
.status-badge-active {
  background: rgba(0, 180, 255, 0.12);
  color: #00b4ff;
  border-color: rgba(0, 180, 255, 0.2);
}

/* 预售标签闪烁 → 蓝色光晕 */
.status-badge-preorder {
  animation: preorder-pulse 2s ease-in-out infinite;
}

@keyframes preorder-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 rgba(0, 180, 255, 0);
  }

  50% {
    box-shadow: 0 0 12px rgba(0, 180, 255, 0.15);
  }
}

/* ---------- 营销徽章：橙/绿/黄 → 品牌蓝 ---------- */

.badge-available {
  background: rgba(0, 180, 255, 0.12);
  color: #00b4ff;
}

.badge-sold {
  background: rgba(255, 107, 107, 0.1);
  color: #ff8f8f;
}

/* ---------- 数值颜色：金/绿 → 品牌蓝 ---------- */

.value-remaining strong,
.period-remaining strong {
  color: #00b4ff;
}

.available-yes {
  color: #00b4ff;
}

.card-status-preorder .spec-value.available-yes,
.card-status-active .spec-value.available-yes {
  color: #00b4ff;
}

/* ---------- 价值状态：绿/橙 → 品牌蓝 ---------- */
.value-status.high,
.value-status.medium {
  background: rgba(0, 180, 255, 0.08);
  color: #00b4ff;
}

.value-status.low {
  background: rgba(0, 180, 255, 0.08);
  color: #00b4ff;
}

/* 列表视图状态：绿色 → 品牌蓝 */
.status-available {
  color: #00b4ff;
}

/* ---------- preOrder & active 操作按钮 → 品牌蓝 ---------- */
.btn-rental .pending {
}

.btn-rental.preOrder {
  background: linear-gradient(135deg, #00b4ff, #0068ff);
  box-shadow: 0 4px 20px rgba(0, 100, 255, 0.2);
}

.btn-rental.active {
  background: linear-gradient(135deg, #00b4ff, #0068ff);
  box-shadow: 0 4px 20px rgba(0, 100, 255, 0.2);
}

.btn-rental.ended {
  background: linear-gradient(135deg, #6b7280, #4b5563);
  box-shadow: 0 4px 20px rgba(0, 100, 255, 0.2);
}

/* ---------- active 倒计时 → 品牌蓝 ---------- */
.card-status-active .countdown-box {
  background: rgba(0, 180, 255, 0.08);
  border-color: rgba(0, 180, 255, 0.15);
}

.card-status-active .countdown-label {
  color: rgba(0, 180, 255, 0.7);
}

.card-status-active .countdown-time {
  color: #00b4ff;
}

/* ---------- preOrder & active 进度条 → 品牌蓝 ---------- */
.card-status-preorder .progress-track,
.card-status-active .progress-track {
  background: rgba(0, 180, 255, 0.08);
}

.card-status-preorder .progress-bar,
.card-status-active .progress-bar {
  background: linear-gradient(90deg, #00b4ff, #0068ff);
}

.btcc {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
}
</style>
